import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/api';
import './ActivityModal.css';

interface ActivityItem {
  timestamp: string;
  action: string;
  details: string;
}

interface ActivityModalProps {
  onClose: () => void;
}

const ACTION_LABELS: Record<string, string> = {
  CREATE_CRATE: 'Created Crate',
  DELETE_CRATE: 'Deleted Crate',
  EMPTY_CRATE: 'Emptied Crate',
  UPLOAD_FILE: 'Uploaded File',
  DOWNLOAD_FILE: 'Downloaded File',
  DELETE_FILE: 'Deleted File',
  RESTORE_FILE: 'Restored File',
  CREATE_SHARE: 'Created Share',
  REVOKE_SHARE: 'Revoked Share',
  CREATE_SECRET: 'Created Secret',
  DELETE_SECRET: 'Deleted Secret',
};

const ACTION_ICONS: Record<string, string> = {
  CREATE_CRATE: '📦',
  DELETE_CRATE: '🗑️',
  EMPTY_CRATE: '🗃️',
  UPLOAD_FILE: '⬆️',
  DOWNLOAD_FILE: '⬇️',
  DELETE_FILE: '🗑️',
  RESTORE_FILE: '♻️',
  CREATE_SHARE: '🔗',
  REVOKE_SHARE: '🚫',
  CREATE_SECRET: '🔐',
  DELETE_SECRET: '🗑️',
};

function formatTimestamp(ts: string): string {
  try {
    const date = new Date(ts);
    return date.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return ts;
  }
}

const ActivityModal: React.FC<ActivityModalProps> = ({ onClose }) => {
  const [activity, setActivity] = useState<ActivityItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await apiService.getActivity(5);
        setActivity(data.activity);
      } catch (e: any) {
        setError('Failed to load activity');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  return (
    <div className="activity-modal-overlay" onClick={onClose}>
      <div className="activity-modal" onClick={e => e.stopPropagation()}>
        <div className="activity-modal-header">
          <h3>Recent Activity</h3>
          <button className="activity-modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="activity-modal-body">
          {loading && <div className="activity-loading">Loading...</div>}
          {error && <div className="activity-error">{error}</div>}
          {!loading && !error && activity.length === 0 && (
            <div className="activity-empty">No recent activity</div>
          )}
          {!loading && !error && activity.length > 0 && (
            <table className="activity-table">
              <thead>
                <tr>
                  <th>Action</th>
                  <th>Details</th>
                  <th>Time</th>
                </tr>
              </thead>
              <tbody>
                {activity.map((item, idx) => (
                  <tr key={idx}>
                    <td className="activity-action">
                      <span className="activity-icon">{ACTION_ICONS[item.action] ?? '📋'}</span>
                      {ACTION_LABELS[item.action] ?? item.action}
                    </td>
                    <td className="activity-details" title={item.details}>
                      {item.details ? item.details.length > 45 ? item.details.slice(0, 45) + '…' : item.details : '—'}
                    </td>
                    <td className="activity-time">{formatTimestamp(item.timestamp)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default ActivityModal;
